// lib/layer-integration.ts
import type { Rect } from '#types/types';
import { QWEN_IMAGE_21_MODEL_ID } from '#lib/qwen-image-21';

export const TASK_AUTO_INTEGRATE_BY_MODEL: Readonly<Record<string, boolean>> = {
  'fater-enhance': false,
  'fater-upscale': false,
  'topaz-image-enhance': false,
  'magnific-image-upscale': false,
  // Measured 2026-09-21: with the geometry exact (±0.5 px on a no-op edit), the model itself drifts 1-3 px on real edits.
  [QWEN_IMAGE_21_MODEL_ID]: true,
  // Same family, same drift, seen on the first real edit once the fal wiring landed: fal returns
  // exactly the image_size we ask for, so what moves is the model, not our geometry.
  'qwen-image-3-pro': true,
  // On an edit Grok 2 sends aspect_ratio auto, so the output follows the input shape and any
  // offset is the model. The snapped ratio only exists on text-to-image, where nothing underneath
  // means no alignment anyway.
  'grok-imagine-2': true,
  // Extended to the scene-preserving edit models once Integrate proved itself on the three above. A refused
  // alignment delivers the stretched layer exactly as before, so a model whose output shape drifts loses nothing.
  'gpt-image-2.5': true,
  'nano-banana-pro': true,
  'nano-banana-2': true,
  'nano-banana-2.1': true,
  'seedream4.5': true,
  'flux-2-pro': true,
  'flux-2-klein-4b': true,
  'qwen-image-2-pro': true,
  'grok-imagine-image': true,
  'muse-image': true,
  'flux-3-image': true,
  'flux-3-klein': true,
  'mai-2.5-flash': true,
  'mai-2.5-pro': true,
  'mai-2.6-flash': true,
  'mai-2.6': true,
};

/** Whether an integrated generation is colour-matched: the generation's explicit value wins for it alone, else the user's setting, off by default. */
export const resolveAutoColorMatch = (requested: boolean | undefined, preference: boolean | undefined): boolean =>
  requested ?? preference === true;

/** Whether a generation's own harmonize parameter recolours it; 'Auto' leaves one covering the whole frame alone. */
export const generationHarmonizeApplies = (mode: unknown, frame: Rect | undefined, generation: Rect): boolean => {
  if (mode === 'On') return true;
  if (mode !== 'Auto' || !frame) return false;
  return !(['x', 'y', 'w', 'h'] as const).every(key => Math.abs(frame[key] - generation[key]) < 1);
};

/** A correction that declines on purpose: the layer is fine, there is just nothing this step can do. Warned, never an error state. */
export class LayerCorrectionRefusal extends Error {}

// Each step takes the previous step's candidate and answers the next one, or null when it declines.
export const runLayerIntegration = async <T>(layer: T, steps: {
  align: (layer: T) => Promise<T | null>;
  harmonize: (layer: T) => Promise<T | null>;
}) => {
  const aligned = await steps.align(layer);
  if (aligned === null) return { status: 'cancelled', step: 'align' } as const;

  // A refused alignment is a bad sign about the whole layer; a colour drift that cannot be measured is not.
  let harmonized: T | null = null;
  let harmonizeError: unknown = null;
  try {
    harmonized = await steps.harmonize(aligned);
  } catch (error) {
    harmonizeError = error;
  }

  return {
    status: 'integrated',
    layer: harmonized ?? aligned,
    harmonizeSkipped: harmonized === null,
    harmonizeError,
  } as const;
};
